import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { buildSpecs, applyLift, DEFAULT_VEHICLE } from "../lib/specs";
import { API } from "../lib/api";
import axios from "axios";

const VehicleContext = createContext(null);
export const useVehicle = () => useContext(VehicleContext);

const LS = "h2-vehicle";
const LS_GARAGE = "tk-garage";
const LS_ACTIVE = "tk-active";
const LS_LIFT = "tk-lift";
const read = (k, fb) => { try { const v = JSON.parse(localStorage.getItem(k)); return v ?? fb; } catch { return fb; } };

const BLANK = { make: "", model: "", year: null };

export function VehicleProvider({ children }) {
  const [catalog, setCatalog] = useState([DEFAULT_VEHICLE]);
  const [selection, setSelection] = useState(() => read(LS, BLANK));
  const [vinInfo, setVinInfo] = useState(null);
  const [garage, setGarage] = useState(() => read(LS_GARAGE, []));
  const [activeGarageId, setActiveGarageId] = useState(() => read(LS_ACTIVE, null));
  const [lift, setLiftState] = useState(() => read(LS_LIFT, "stock"));

  useEffect(() => {
    axios.get(`${API}/vehicles`).then((r) => {
      if (Array.isArray(r.data) && r.data.length) setCatalog(r.data);
    }).catch(() => {});
  }, []);

  useEffect(() => { localStorage.setItem(LS, JSON.stringify(selection)); }, [selection]);
  useEffect(() => { localStorage.setItem(LS_GARAGE, JSON.stringify(garage)); }, [garage]);
  useEffect(() => { localStorage.setItem(LS_ACTIVE, JSON.stringify(activeGarageId)); }, [activeGarageId]);
  useEffect(() => { localStorage.setItem(LS_LIFT, JSON.stringify(lift)); }, [lift]);

  const makes = useMemo(() => [...new Set(catalog.map((v) => v.make))].sort(), [catalog]);
  const modelsFor = (make) => catalog.filter((v) => v.make === make).map((v) => v.model);
  const recordFor = (make, model) => catalog.find((v) => v.make === make && v.model === model);
  const yearsFor = (make, model) => {
    const rec = recordFor(make, model);
    if (!rec) return [];
    const [a, b] = rec.years || [rec.year, rec.year];
    const out = [];
    for (let y = b; y >= a; y--) out.push(y);
    return out;
  };

  const selected = useMemo(() => {
    const rec = recordFor(selection.make, selection.model);
    if (!rec) return null;
    const [a, b] = rec.years || [rec.year, rec.year];
    const year = selection.year >= a && selection.year <= b ? selection.year : b;
    return { ...rec, year };
  }, [catalog, selection]);

  const activeEntry = useMemo(() => garage.find((e) => e.id === activeGarageId) || null, [garage, activeGarageId]);
  const isCustomActive = activeEntry?.source === "custom";

  const baseVehicle = useMemo(() => (isCustomActive ? activeEntry.customVehicle : selected), [isCustomActive, activeEntry, selected]);
  const hasVehicle = !!baseVehicle;
  const rawSpecs = useMemo(() => (baseVehicle ? buildSpecs(baseVehicle) : null), [baseVehicle]);
  const specs = useMemo(() => applyLift(rawSpecs, lift), [rawSpecs, lift]);
  const vehicleLabel = baseVehicle ? `${baseVehicle.year ?? ""} ${baseVehicle.make} ${baseVehicle.model}`.trim() : "";
  const drivetrain = baseVehicle?.drivetrain || "";

  const exitSaved = () => setActiveGarageId(null);

  const setMake = (make) => { setVinInfo(null); exitSaved(); setSelection({ make, model: "", year: null }); };
  const setModel = (model) => {
    setVinInfo(null); exitSaved();
    const ys = yearsFor(selection.make, model);
    setSelection((s) => ({ ...s, model, year: ys[0] ?? null }));
  };
  const setYear = (year) => { setVinInfo(null); exitSaved(); setSelection((s) => ({ ...s, year: Number(year) })); };

  const applyDecoded = (decoded) => {
    const { make, model, year } = decoded;
    const ml = (make || "").toLowerCase();
    const makeRec = makes.find((mk) => mk.toLowerCase() === ml)
      || makes.find((mk) => ml.includes(mk.toLowerCase()) || mk.toLowerCase().includes(ml));
    if (!makeRec) return { ok: false, decoded: { make, model, year } };

    const models = modelsFor(makeRec);
    const dl = (model || "").toLowerCase();
    const first = dl.split(/\s+/)[0];
    const modelRec = models.find((mo) => mo.toLowerCase() === dl)
      || models.find((mo) => mo.toLowerCase().startsWith(first) || first.startsWith(mo.toLowerCase().split(/\s+/)[0]))
      || models.find((mo) => dl.includes(mo.toLowerCase()) || mo.toLowerCase().includes(dl));
    if (!modelRec) return { ok: false, makeRec, decoded: { make, model, year } };

    const ys = yearsFor(makeRec, modelRec);
    let chosenYear = ys[0] ?? null;
    if (year && ys.length) {
      if (ys.includes(Number(year))) chosenYear = Number(year);
      else chosenYear = year > ys[0] ? ys[0] : ys[ys.length - 1];
    }
    exitSaved();
    setSelection({ make: makeRec, model: modelRec, year: chosenYear });
    setVinInfo({ vin: decoded.vin, trim: decoded.trim, engine: decoded.engine, fuel: decoded.fuel, drive: decoded.drive, bodyClass: decoded.bodyClass });
    return { ok: true, make: makeRec, model: modelRec, year: chosenYear };
  };

  // ---- Multi-vehicle garage ----
  const saveCurrentToGarage = () => {
    if (!selected) return null;
    const label = `${selected.year} ${selected.make} ${selected.model}`;
    const existing = garage.find((e) => e.source === "catalog" && e.make === selected.make && e.model === selected.model && e.year === selected.year);
    if (existing) {
      setGarage((g) => g.map((e) => (e.id === existing.id ? { ...e, lift, vinInfo } : e)));
      setActiveGarageId(existing.id);
      setLiftState(lift);
      return existing;
    }
    const id = `g-${Date.now()}`;
    const entry = { id, source: "catalog", label, make: selected.make, model: selected.model, year: selected.year, vinInfo, lift };
    setGarage((g) => [entry, ...g]);
    setActiveGarageId(id);
    return entry;
  };

  const addCustomVehicle = (d) => {
    const id = `custom-${Date.now()}`;
    const t = (v) => Number(v) || 0;
    const camRange = (v) => [t(v), +(t(v) - 0.5).toFixed(2), +(t(v) + 0.5).toFixed(2)];
    const toeRange = (v) => [t(v), +(t(v) - 0.05).toFixed(2), +(t(v) + 0.05).toFixed(2)];
    const cv = {
      id, make: d.make.trim(), model: d.model.trim(), year: Number(d.year) || null, drivetrain: d.drivetrain || "Custom",
      front: { camber: camRange(d.frontCamber), caster: camRange(d.frontCaster), toe: toeRange(d.frontToe) },
      rear: { camber: camRange(d.rearCamber), toe: toeRange(d.rearToe), adjustable: !!d.rearAdjustable },
    };
    const entry = { id, source: "custom", label: `${cv.year ?? ""} ${cv.make} ${cv.model}`.trim(), customVehicle: cv, lift: "stock" };
    setGarage((g) => [entry, ...g]);
    setActiveGarageId(id);
    setVinInfo(null);
    setLiftState("stock");
    return entry;
  };

  const removeFromGarage = (id) => {
    setGarage((g) => g.filter((e) => e.id !== id));
    if (activeGarageId === id) setActiveGarageId(null);
  };

  const activateGarageVehicle = (id) => {
    const entry = garage.find((e) => e.id === id);
    if (!entry) return;
    setActiveGarageId(id);
    setLiftState(entry.lift || "stock");
    if (entry.source === "catalog") {
      setSelection({ make: entry.make, model: entry.model, year: entry.year });
      setVinInfo(entry.vinInfo || null);
    } else {
      setVinInfo(null);
    }
  };

  const setLift = (preset) => {
    setLiftState(preset);
    if (activeGarageId) setGarage((g) => g.map((e) => (e.id === activeGarageId ? { ...e, lift: preset } : e)));
  };

  const value = {
    catalog, selection, selected, hasVehicle, specs, vehicleLabel, drivetrain, vinInfo,
    makes, modelsFor, yearsFor, setMake, setModel, setYear, applyDecoded,
    garage, activeGarageId, activeEntry, isCustomActive, lift,
    saveCurrentToGarage, addCustomVehicle, removeFromGarage, activateGarageVehicle, setLift,
  };
  return <VehicleContext.Provider value={value}>{children}</VehicleContext.Provider>;
}
